import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { useDeleteFacultyMutation, useFacultiesListQuery } from "api/faculty/faculty.mutation";
import ExcelIcon from "assets/admin/excel.svg";
import { ConfirmDeleteModal } from "components/ConfirmDeleteModal";
import SearchField from "components/SearchField";
import { Table } from "components/Table";
import { exportToExcel } from "constants/export";
import { handleError } from "functions/apiHandleError";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreateUpdateFacultyModal } from "./components/createUpdateModal";
import { FacultyFieldsModal } from "./components/fieldOfStudies";
import classes from "./style.module.scss";

const Faculties = () => {
  const [searchParams] = useSearchParams();
  const paramPage = Number(searchParams.get("page")) || 1;

  const [searchValue, setSearchValue] = useState("");
  const [pageFilter, setPageFilter] = useState({
    page: paramPage,
    page_size: 15,
    total: 1,
    count: 0,
  });
  const [createUpdateModal, setCreateUpdateModal] = useState({
    open: false,
    selectedFaculty: null,
  });
  const [facultyFieldsModal, setFacultyFieldsModal] = useState({
    open: false,
    selectedFaculty: null,
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, selectedFaculty: null });

  const facultiesQuery = useFacultiesListQuery({
    page: pageFilter.page,
    page_size: pageFilter.page_size,
    search: searchValue,
  });
  const deleteFacultyMutation = useDeleteFacultyMutation();
  const tableData = facultiesQuery.data?.results ?? [];
  const totalPages = facultiesQuery.data?.total ?? pageFilter.total;

  const handleDeleteFaculty = async () => {
    const facultyID = deleteModal.selectedFaculty?.id;
    if (!facultyID) return;

    try {
      await deleteFacultyMutation.mutateAsync(facultyID);
      setDeleteModal({ open: false, selectedFaculty: null });
    } catch (err) {
      handleError({ err });
    }
  };
  const handleCloseCreateUpdateModal = () => {
    setCreateUpdateModal({
      open: false,
      selectedFaculty: null,
    });
  };
  const handleCloseFacultyFieldsModal = () => {
    setFacultyFieldsModal({
      open: false,
      selectedFaculty: null,
    });
  };

  useEffect(() => {
    if (pageFilter.page !== paramPage) {
      setPageFilter((prev) => ({
        ...prev,
        page: paramPage,
      }));
    }
  }, [paramPage]);

  const columns = [
    {
      field: "fa_name",
      headerName: "نام فارسی",
      minWidth: 130,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "en_name",
      headerName: "نام انگلیسی",
      minWidth: 120,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "ar_name",
      headerName: "نام عربی",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "is_active",
      headerName: "فعال؟",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderExport: ({ row }) => (row?.is_active ? "بله" : "خیر"),
      renderCell: ({ row }) => (
        <IconButton color={row.is_active ? "success" : "error"}>
          {row.is_active ? (
            <CheckCircleIcon fontSize="medium" />
          ) : (
            <HighlightOffIcon fontSize="medium" />
          )}
        </IconButton>
      ),
    },
    {
      field: "actions",
      headerName: "عملیات",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      hideInExport: true,
      renderCell: ({ row }) => (
        <Stack spacing={2} direction="row">
          <Tooltip title="ویرایش">
            <IconButton
              color="primary"
              onClick={() =>
                setCreateUpdateModal({
                  open: true,
                  selectedFaculty: row,
                })
              }
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="حذف">
            <IconButton
              color="error"
              onClick={() => setDeleteModal({ open: true, selectedFaculty: row })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: "field_of_studies",
      headerName: "رشته ها",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      hideInExport: true,
      renderCell: ({ row }) => (
        <div
          className={classes.nextBox}
          onClick={() =>
            setFacultyFieldsModal({
              open: true,
              selectedFaculty: row.id,
            })
          }
        >
          <ArrowBackIosNewIcon className={classes.nextIcon} fontSize="small" />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={classes.mainDiv}>
        <div className={classes.toolBar}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <SearchField setValueCallback={setSearchValue} variant="standard" />
            </Grid>
            <Grid item xs={0} md={4} />
            <Grid item xs={12} md={4}>
              <div className={classes.manageBar}>
                <Tooltip title="ایجاد دانشکده">
                  <IconButton
                    onClick={() => setCreateUpdateModal({ open: true, selectedFaculty: null })}
                    color="success"
                  >
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="خروجی اکسل">
                  <IconButton
                    onClick={() => exportToExcel(columns, tableData, "faculties.xlsx")}
                    color="success"
                  >
                    <img src={ExcelIcon} alt="export-excell" className={classes.customIcon} />
                  </IconButton>
                </Tooltip>
              </div>
            </Grid>
          </Grid>
        </div>
        <Table
          rows={tableData}
          columns={columns}
          totalPages={totalPages}
          loading={facultiesQuery.isFetching || deleteFacultyMutation.isPending}
          className={classes.tableContainer}
        />
      </div>
      <CreateUpdateFacultyModal
        open={createUpdateModal.open}
        onClose={handleCloseCreateUpdateModal}
        defaultData={createUpdateModal.selectedFaculty}
      />
      <FacultyFieldsModal
        open={facultyFieldsModal.open}
        onClose={handleCloseFacultyFieldsModal}
        facultyID={facultyFieldsModal.selectedFaculty}
      />
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, selectedFaculty: null })}
        onConfirm={handleDeleteFaculty}
        loading={deleteFacultyMutation.isPending}
        title="حذف دانشکده"
        message="آیا از حذف دانشکده انتخاب‌شده مطمئن هستید؟ با حذف آن، اطلاعات وابسته نیز ممکن است در دسترس نباشند."
      />
    </>
  );
};

export default Faculties;
